import React from "react";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import classes from "./DatePricingBar.module.css";
import {
  DateResolution,
  TIER_LABELS,
  TIER_MONTHS,
  formatLongDate,
} from "./pricing";
import { Availability } from "./useBookedDates";

export const PRICING_SHEET_URL = "/pricing-sheet.pdf";

// `loading` and `unknown` stay silent: a visitor who can't be told anything
// useful is better served by no message than by a spinner or a warning.
const AvailabilityNote = ({ availability }: { availability: Availability }) => {
  if (availability === "available") {
    return (
      <p className={`${classes.availability} ${classes.open}`}>
        This date is open
      </p>
    );
  }
  if (availability === "booked") {
    return (
      <p className={`${classes.availability} ${classes.booked}`}>
        This date is already booked.{" "}
        <Link to="/booking">See our availability calendar</Link> to find open
        dates.
      </p>
    );
  }
  return null;
};

const STATUS_MESSAGE: Record<string, string> = {
  closed: "We're closed for the winter from December 1 through February 28.",
  past: "Choose a date in the future to see pricing.",
  unpriced:
    "We haven't published pricing for this year yet — get in touch and we'll quote it.",
};

const DatePricingBar = ({
  date,
  onDateChange,
  resolution,
  availability,
  minDate,
  isDateSelectable,
}: {
  date: Date;
  onDateChange: (date: Date) => void;
  resolution: DateResolution;
  availability: Availability;
  minDate: Date;
  isDateSelectable: (date: Date) => boolean;
}) => {
  const context = resolution.status === "ok" ? resolution.context : null;

  return (
    <div className={classes.bar}>
      <div className={classes.inner}>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Your event date</span>
          <DatePicker
            selected={date}
            onChange={(next) => next && onDateChange(next)}
            minDate={minDate}
            filterDate={isDateSelectable}
            dateFormat="EEEE, MMMM d, yyyy"
            popperPlacement="bottom-start"
            className={classes.input}
            aria-label={`Event date, currently ${formatLongDate(date)}`}
          />
          <span className={classes.stayHint}>
            Staying more than one day? Choose your dates when you get a quote.
          </span>
        </div>

        {context ? (
          <div className={classes.summary}>
            <p className={classes.season}>
              {TIER_LABELS[context.tier]}
              <span className={classes.months}>
                {" "}
                ({TIER_MONTHS[context.tier]})
              </span>
              <span className={classes.divider}>·</span>
              <span className={classes.rate}>
                {context.dayType === "weekend"
                  ? "Friday–Sunday rate"
                  : "Monday–Thursday rate"}
              </span>
            </p>
            <AvailabilityNote availability={availability} />
          </div>
        ) : (
          <p className={classes.status}>
            {STATUS_MESSAGE[resolution.status] ?? ""}
          </p>
        )}
      </div>

      <p className={classes.sheetLink}>
        To see full pricing info for all times of year please see our{" "}
        <a href={PRICING_SHEET_URL} target="_blank" rel="noreferrer">
          pricing sheet
        </a>
        .
      </p>
    </div>
  );
};

export default DatePricingBar;
